import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";

import activeSubscriptionApi from "../../hooks/GET/activeSubscription";
import subscriptionSettingsApi from "../../hooks/GET/subscriptionSettings";
import SubscriptionDialog from "./SubscriptionDialog";
import SubscriptionEditDialog from "./SubscriptionEditDialog";
import SubscriptionPlanDialog from "./SubscriptionPlanDialog";
import { endpoint } from "../../config";
import { customToast } from "../../lib/toastLib";
import { formatLocalDateTime } from "../../utils/dateTime";

const initialSubscriptionSettingsState = {
  fetchCompleted: false,
  subscriptionMode: "basic",
  basicSubscriptionStatus: "active",
  message: "",
};

const initialSubscriptionSettingsFormState = {
  subscriptionMode: "basic",
  basicSubscriptionStatus: "active",
};

function Subscription() {
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get("subscriptionId");

  useEffect(() => {
    if (subscriptionId) {
      axios
        .put(`${endpoint}/api/subscription/${subscriptionId}`)
        .then((res) => res.data)
        .then((data) => {
          customToast.success(data.message);
          navigate("/dashboard", { replace: true });
        })
        .catch((error) => {
          if (error.response) {
            customToast.error(error.response.data?.message);
          } else {
            customToast.error(error.message);
          }
        });
    }
  }, [subscriptionId, navigate]);

  const [subscriptionSettingsState, setSubscriptionSettingsState] = useState(
    initialSubscriptionSettingsState
  );
  const [subscriptionSettingsForm, setSubscriptionSettingsForm] = useState(
    initialSubscriptionSettingsFormState
  );
  const [subscriptionSettingsSaving, setSubscriptionSettingsSaving] =
    useState(false);

  const syncSubscriptionSettingsState = (data) => {
    setSubscriptionSettingsState({
      fetchCompleted: true,
      subscriptionMode: data.subscriptionMode || "subscription",
      basicSubscriptionStatus: data.basicSubscriptionStatus || null,
      message: data.message || "",
    });
    setSubscriptionSettingsForm({
      subscriptionMode: data.subscriptionMode || "subscription",
      basicSubscriptionStatus: data.basicSubscriptionStatus || "active",
    });
  };

  const fetchSubscriptionSettings = () => {
    subscriptionSettingsApi()
      .then(syncSubscriptionSettingsState)
      .catch(() => {
        setSubscriptionSettingsState((currentValue) => ({
          ...currentValue,
          fetchCompleted: true,
        }));
      });
  };

  useEffect(() => {
    if (user.id && user.isAdmin) {
      fetchSubscriptionSettings();
    }
  }, [user.id, user.isAdmin]);

  const [activeSubscription, setActiveSubscription] = useState({
    id: "",
    fetchCompleted: false,
    endDate: null,
    planName: "",
    planPrice: "",
    planCurrenceyIsoCode: "",
    planPeriod: "",
    status: "",
    subscriptionDays: null,
    allowPay: false,
  });

  useEffect(() => {
    if (
      !subscriptionSettingsState.fetchCompleted ||
      subscriptionSettingsState.subscriptionMode !== "subscription"
    ) {
      setActiveSubscription((currentValue) => ({
        ...currentValue,
        fetchCompleted: false,
      }));
      return;
    }

    activeSubscriptionApi()
      .then((data) => {
        setActiveSubscription({
          id: data.subscription.id,
          fetchCompleted: true,
          endDate: data.subscription.endDate,
          planName: data.subscription.planName,
          planPrice: data.subscription.planPrice,
          planCurrenceyIsoCode: data.subscription.planCurrecyIsoCode,
          planPeriod: data.subscription.planBillingInterval,
          status: data.status,
          subscriptionDays: data.subscriptionDays,
          allowPay: data.subscriptionDays > 30 ? false : true,
        });
      })
      .catch(() => {});
  }, [
    subscriptionSettingsState.fetchCompleted,
    subscriptionSettingsState.subscriptionMode,
  ]);

  const renderMainText = (firstTextPart, lastTextPart) => (
    <>
      {firstTextPart}
      <Typography
        variant="inherit"
        color="secondary.main"
        sx={{ display: "contents" }}
      >
        {` ${lastTextPart}`}
      </Typography>
    </>
  );

  const [subscriptionDialogState, setSubscriptionDialogState] = useState(false);
  const [subscriptionEditDialogState, setSubscriptionEditDialogState] =
    useState(false);
  const [subscriptionPlanDialogState, setSubscriptionPlanDialogState] =
    useState(false);

  const handleSubscriptionDialogOpen = () => {
    setSubscriptionDialogState(true);
  };

  const handleSubscriptionDialogClose = () => {
    setSubscriptionDialogState(false);
  };

  const handleSubscriptionEditDialogOpen = () => {
    setSubscriptionEditDialogState(true);
  };

  const handleSubscriptionEditDialogClose = () => {
    setSubscriptionEditDialogState(false);
  };

  const handleSubscriptionPlanDialogOpen = () => {
    setSubscriptionPlanDialogState(true);
  };

  const handleSubscriptionPlanDialogClose = () => {
    setSubscriptionPlanDialogState(false);
  };

  const handleSubscriptionModeChange = (event) => {
    const subscriptionMode = event.target.value;

    setSubscriptionSettingsForm((currentValue) => ({
      ...currentValue,
      subscriptionMode,
      basicSubscriptionStatus:
        subscriptionMode === "basic"
          ? currentValue.basicSubscriptionStatus || "active"
          : "active",
    }));
  };

  const handleBasicSubscriptionStatusChange = (event) => {
    const basicSubscriptionStatus = event.target.value;

    setSubscriptionSettingsForm((currentValue) => ({
      ...currentValue,
      basicSubscriptionStatus,
    }));
  };

  const handleSubscriptionSettingsSave = () => {
    setSubscriptionSettingsSaving(true);

    axios
      .put(`${endpoint}/api/subscription-settings`, {
        subscriptionMode: subscriptionSettingsForm.subscriptionMode,
        basicSubscriptionStatus:
          subscriptionSettingsForm.subscriptionMode === "basic"
            ? subscriptionSettingsForm.basicSubscriptionStatus
            : null,
      })
      .then((res) => res.data)
      .then((data) => {
        syncSubscriptionSettingsState(data);
        customToast.success("Subscription settings updated.");
      })
      .catch((error) => {
        if (error.response) {
          customToast.error(error.response.data?.message);
        } else {
          customToast.error(error.message);
        }
      })
      .finally(() => {
        setSubscriptionSettingsSaving(false);
      });
  };

  const renderSubscriptionSettingsSection = () => {
    if (!user.isDefault) {
      return null;
    }

    return (
      <Box sx={{ marginBottom: 3, textAlign: "left" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
          Portal Mode
        </Typography>
        <FormControl fullWidth>
          <RadioGroup
            value={subscriptionSettingsForm.subscriptionMode}
            onChange={handleSubscriptionModeChange}
          >
            <FormControlLabel
              value="subscription"
              control={<Radio />}
              label="Subscription Portal"
            />
            <FormControlLabel
              value="basic"
              control={<Radio />}
              label="Basic Portal"
            />
          </RadioGroup>
        </FormControl>

        {subscriptionSettingsForm.subscriptionMode === "basic" && (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Basic Portal Status
            </Typography>
            <FormControl fullWidth>
              <RadioGroup
                value={subscriptionSettingsForm.basicSubscriptionStatus}
                onChange={handleBasicSubscriptionStatusChange}
              >
                <FormControlLabel
                  value="active"
                  control={<Radio />}
                  label="Active"
                />
                <FormControlLabel
                  value="disabled"
                  control={<Radio />}
                  label="Disabled"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        <LoadingButton
          variant="contained"
          color="secondary"
          onClick={handleSubscriptionSettingsSave}
          loading={subscriptionSettingsSaving}
          sx={{ marginTop: 2 }}
        >
          Save
        </LoadingButton>
        <Divider sx={{ marginTop: 3 }} />
      </Box>
    );
  };

  const renderAdminBasicPortalView = () => {
    return (
      <Grid
        item
        component={Paper}
        variant="outlined"
        xs={12}
        sm={8}
        md={6}
        sx={{ padding: 2, textAlign: "center" }}
      >
        <Box sx={{ padding: 2, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {subscriptionSettingsState.message
              ? renderMainText("Your subscription is", "Active")
              : renderMainText("Your subscription is", "Active")}
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderBasicPortalStatusCard = () => (
    <Grid
      item
      component={Paper}
      variant="outlined"
      xs={12}
      sm={8}
      md={6}
      sx={{ padding: 2, textAlign: "center" }}
    >
      {renderSubscriptionSettingsSection()}
      <Typography variant="h6" gutterBottom>
        {renderMainText(
          "Basic portal is",
          `${
            subscriptionSettingsState.basicSubscriptionStatus
              ? subscriptionSettingsState.basicSubscriptionStatus.charAt(0).toUpperCase() +
                subscriptionSettingsState.basicSubscriptionStatus.slice(1)
              : ""
          }.`
        )}
      </Typography>
    </Grid>
  );

  const renderSubscriptionContent = () => (
    <>
      <Grid
        item
        component={Paper}
        variant="outlined"
        sx={{ padding: 2, textAlign: "center" }}
        xs={12}
        sm={8}
        md={6}
      >
        {renderSubscriptionSettingsSection()}
        <Typography variant="h6" gutterBottom>
          {activeSubscription.fetchCompleted ? (
            activeSubscription.status === "expired" ? (
              renderMainText("Your subscription has been", "Expired")
            ) : activeSubscription.status === "free-trial" ? (
              renderMainText(
                "Your subscription has been expired and you are under 14 days free-trial period out of which",
                `${Math.abs(activeSubscription.subscriptionDays)} day/s are used`
              )
            ) : activeSubscription.status === "active" &&
              activeSubscription.subscriptionDays <= 30 ? (
              renderMainText(
                "Your subscription is about to expire in",
                `${activeSubscription.subscriptionDays} day/s`
              )
            ) : activeSubscription.status === "active" &&
              activeSubscription.subscriptionDays > 30 ? (
              renderMainText("Your subscription is", "Active")
            ) : (
              ""
            )
          ) : (
            <Skeleton animation="wave" variant="rectangular" />
          )}
        </Typography>
        {activeSubscription.subscriptionDays <= 30 && (
          <Typography color="secondary.main" gutterBottom variant="subtitle2">
            {activeSubscription.fetchCompleted ? (
              activeSubscription.status === "expired" ? (
                "Pay now to get your account unblocked from accessing the platform services"
              ) : (
                "Pay now before the account gets blocked from accessing the platform services"
              )
            ) : (
              <Skeleton animation="wave" variant="rectangular" />
            )}
          </Typography>
        )}
        {activeSubscription.fetchCompleted ? (
          <Box sx={{ marginTop: 3 }}>
            <Typography variant="body2">
              Name: {activeSubscription.planName}
            </Typography>
            <Typography variant="body2">
              Price: {activeSubscription.planPrice}{" "}
              {activeSubscription.planCurrenceyIsoCode.toUpperCase()}
            </Typography>
            <Typography variant="body2">
              Period: {activeSubscription.planPeriod} months
            </Typography>
            <Typography variant="body2">
              Due Date: {formatLocalDateTime(activeSubscription.endDate)}
            </Typography>
          </Box>
        ) : (
          <Skeleton
            animation="wave"
            variant="rectangular"
            height={100}
            sx={{ marginTop: 3 }}
          />
        )}
        <Button
          color="secondary"
          sx={{
            textDecoration: "underline",
            textTransform: "capitalize",
            marginTop: 3,
          }}
          onClick={handleSubscriptionDialogOpen}
        >
          Subscription History
        </Button>
        {user.isDefault && (
          <Button
            color="secondary"
            sx={{
              textDecoration: "underline",
              textTransform: "capitalize",
              marginTop: 3,
            }}
            onClick={handleSubscriptionEditDialogOpen}
          >
            Edit Subscription
          </Button>
        )}
        {activeSubscription.allowPay && (
          <Button
            color="secondary"
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubscriptionPlanDialogOpen}
          >
            Pay
          </Button>
        )}
      </Grid>

      <SubscriptionDialog
        open={subscriptionDialogState}
        handleClose={handleSubscriptionDialogClose}
      />

      <SubscriptionEditDialog
        open={subscriptionEditDialogState}
        activeSubscription={activeSubscription}
        handleClose={handleSubscriptionEditDialogClose}
      />

      <SubscriptionPlanDialog
        open={subscriptionPlanDialogState}
        handleClose={handleSubscriptionPlanDialogClose}
      />
    </>
  );

  return (
    <Grid container justifyContent="center" spacing={2}>
      {!subscriptionSettingsState.fetchCompleted ? (
        <Grid item xs={12} sm={8} md={6}>
          <Skeleton animation="wave" variant="rectangular" height={220} />
        </Grid>
      ) : (
        <>
          {subscriptionSettingsState.subscriptionMode === "subscription" ? (
            renderSubscriptionContent()
          ) : user.isDefault ? (
            renderBasicPortalStatusCard()
          ) : (
            renderAdminBasicPortalView()
          )}
        </>
      )}
    </Grid>
  );
}

export default Subscription;
